import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { APIResponse } from '../types';

type OktaTokenStorage = {
  accessToken?: {
    accessToken?: string;
  };
};

export class ApiError<T = unknown> extends Error {
  constructor(public message: string, public status: number = 500, public data?: T) {
    super(message);
    this.name = 'ApiError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

const getAccessToken = (): string | null => {
  const oktaTokenStorage = localStorage.getItem('okta-token-storage');
  if (oktaTokenStorage) {
    let oktaToken: OktaTokenStorage = JSON.parse(oktaTokenStorage) as OktaTokenStorage;
    let value;
    if (oktaToken.accessToken) {
      value = oktaToken.accessToken.accessToken;
    }
    return value ? `Bearer ${value}` : null;
  }
  return null;
};

const httpClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers.Authorization = accessToken;
  }
  return config;
});

httpClient.interceptors.response.use(
  (response: AxiosResponse<APIResponse>) => {
    const success =
      typeof response.data.success === 'boolean'
        ? response.data.success
        : response.status >= 200 && response.status < 300;
    if (response.data.token) localStorage.setItem('token', response.data.token);
    return {
      ...response,
      data: {
        success,
        message: response.data.message,
        data: response.data,
        status: response.status,
      },
    };
  },
  (error: AxiosError<APIResponse>): Promise<never> => {
    const status = error.response?.status ?? 500;
    const message = error.response?.data.message || error.message;
    const errorData = error.response?.data.data;

    if (status === 401) {
      localStorage.removeItem('okta-token-storage');
      window.location.href = '/login';
    }

    return Promise.reject(new ApiError(message, status, errorData));
  }
);

export function handleApiResponse<T>(response: AxiosResponse<APIResponse<T>>): T {
  const status = response.status;
  if (!(status >= 200 && status < 300)) {
    throw new ApiError(response.data.message, response.status, response.data);
  }

  if (typeof response.data === 'undefined') {
    throw new ApiError('No data received', response.status);
  }

  return response.data as T;
}

export function handleApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (axios.isAxiosError<APIResponse>(error)) {
    return new ApiError(
      error.response?.data.message || error.message,
      error.response?.status,
      error.response?.data.data
    );
  }

  if (error instanceof Error) {
    return new ApiError(error.message);
  }

  return new ApiError('Unknown error occurred');
}

if (import.meta.env.DEV) {
  httpClient.interceptors.request.use((config) => {
    const method = (config.method?.toUpperCase() ?? 'GET').padEnd(7);
    const url = config.url ?? '/unknown-endpoint';
    console.log(`Request: ${method} ${url}`);
    return config;
  });

  httpClient.interceptors.response.use(
    (response) => {
      const status = response.status.toString().padStart(3);
      const url = response.config.url ?? '/unknown-endpoint';
      console.log(`Response: ${status} ${url}`);
      return response;
    },
    (error) => {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error(`API Error: ${message}`);
      return Promise.reject(error instanceof Error ? error : new ApiError(message));
    }
  );
}

export default httpClient;
